import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import Estimation from "@/pages/Estimation";
import { JustePrixGame } from "@/pages/Game";
import { PriceMap } from "@/pages/PriceMap";
import { LatestArticles } from "@/components/LatestArticles";
import { CustomProject } from "@/components/CustomProject";
import { Marquee } from "@/components/Marquee";
import {
  Eye,
  FileText,
  MapPin,
  ShieldCheck,
  Zap,
  ArrowRight,
  Building2,
  Home,
  Mountain,
  Gamepad2,
  Newspaper,
  Sparkles,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const LYON_IMG =
  "https://images.pexels.com/photos/16632292/pexels-photo-16632292.jpeg?auto=compress&cs=tinysrgb&w=1200";
const CHALET_IMG =
  "https://images.unsplash.com/photo-1610803523148-a0052fa341bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const HERO_SLIDES = [
  { img: LYON_IMG, city: "Lyon", pricem2: "5 200 €/m²" },
  {
    img: "https://customer-assets-v7afamib.emergentagent.net/job_property-eyes-region/artifacts/89uczeyu_La_Vieille_Ville_d_Annecy-Gilles_Piel-10277-900x675.jpg",
    city: "Annecy",
    pricem2: "5 500 €/m²",
  },
  {
    img: "https://customer-assets-v7afamib.emergentagent.net/job_property-eyes-region/artifacts/67ifvmik_residence_etudiante_uxco_student_grenoble-2-684x1024.jpg",
    city: "Grenoble",
    pricem2: "2 950 €/m²",
  },
  {
    img: "https://customer-assets-v7afamib.emergentagent.net/job_property-eyes-region/artifacts/59nvajr9_auvergne-clermont-ferrand-hd.jpg",
    city: "Clermont-Ferrand",
    pricem2: "2 230 €/m²",
  },
];

function HeroSlideshow() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % HERO_SLIDES.length), 3800);
    return () => clearInterval(t);
  }, []);
  const slide = HERO_SLIDES[i];
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="relative"
      data-testid="hero-slideshow"
    >
      <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200 h-[480px]">
        <AnimatePresence mode="wait">
          <motion.img
            key={i}
            src={slide.img}
            alt={slide.city}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-slate-900/20" />
        <div className="absolute top-4 right-4 flex gap-1.5">
          {HERO_SLIDES.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
          ))}
        </div>
      </div>
      <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 w-56 border border-slate-100">
        <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Prix moyen au m²</div>
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
            <div className="font-display text-2xl font-extrabold text-slate-900">{slide.pricem2}</div>
            <div className="text-xs text-slate-500 mt-1">{slide.city}</div>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Landing() {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, []);
  return (
    <div className="App" data-testid="landing-page">
      <Header />
      <Marquee />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div
              variants={fade} initial="hidden" animate="show"
              className="inline-flex items-center gap-2 bg-blue-50 text-[#2563EB] px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6"
            >
              <MapPin className="w-3.5 h-3.5" strokeWidth={2} /> Spécialiste Auvergne-Rhône-Alpes
            </motion.div>
            <motion.h1
              variants={fade} initial="hidden" animate="show" custom={1}
              className="font-display text-5xl lg:text-6xl tracking-tighter font-extrabold text-slate-900 leading-[1.05]"
            >
              Connaissez-vous la <span className="text-[#2563EB]">vraie valeur</span> de votre bien ?
            </motion.h1>
            <motion.p
              variants={fade} initial="hidden" animate="show" custom={2}
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Un avis de valeur clair, gratuit et 100% indépendant. Profitez d'une estimation
              sans pression commerciale pour garder le contrôle total de votre projet immobilier.
            </motion.p>
            <motion.div
              variants={fade} initial="hidden" animate="show" custom={3}
              className="mt-8 flex flex-wrap gap-4"
            >
              <a
                href="#estimation"
                data-testid="hero-estimate-btn"
                className="group bg-[#2563EB] text-white hover:bg-blue-700 px-7 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/20 hover:shadow-xl flex items-center gap-2"
              >
                Estimer gratuitement
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </a>
              <Link
                to="/actualites"
                data-testid="hero-actualites-btn"
                className="group border border-slate-300 bg-white hover:bg-slate-50 text-slate-900 px-7 py-4 rounded-xl font-semibold transition-all flex items-center gap-2"
              >
                <Newspaper className="w-5 h-5 text-slate-500" strokeWidth={2} />
                Actualités &amp; Conseils Immo
              </Link>
            </motion.div>
            <motion.div
              variants={fade} initial="hidden" animate="show" custom={3.5}
              className="mt-4"
            >
              <a
                href="#projet"
                data-testid="hero-projet-btn"
                className="group inline-flex items-center gap-2 bg-[#C85A40] text-white hover:bg-[#b04d35] px-7 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-orange-500/20"
              >
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2} />
                Votre projet immobilier sur mesure
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </a>
            </motion.div>
            <motion.div
              variants={fade} initial="hidden" animate="show" custom={4}
              className="mt-4 flex flex-wrap gap-3"
            >
              <a
                href="#jeu"
                data-testid="hero-game-btn"
                className="group inline-flex items-center gap-2 bg-[#C85A40] text-white hover:bg-[#b04d35] px-5 py-3 rounded-xl font-semibold transition-all shadow-lg shadow-orange-500/20"
              >
                <Gamepad2 className="w-5 h-5" strokeWidth={2} />
                Le Juste Prix Immo — Jouez !
              </a>
              <a
                href="#carte"
                data-testid="hero-map-btn"
                className="inline-flex items-center gap-2 border-2 border-[#2563EB] text-[#2563EB] hover:bg-blue-50 px-5 py-3 rounded-xl font-semibold transition-all"
              >
                <MapPin className="w-5 h-5" strokeWidth={2} />
                Découvrez le prix au m²
              </a>
            </motion.div>
            <motion.div
              variants={fade} initial="hidden" animate="show" custom={4}
              className="mt-10 flex items-center gap-6 text-sm text-slate-500"
            >
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2563EB]" strokeWidth={2} /> 100% gratuit</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#2563EB]" strokeWidth={2} /> Résultat immédiat</span>
            </motion.div>
          </div>

          <HeroSlideshow />
        </div>
      </section>

      {/* COMMENT CA MARCHE */}
      <section id="comment" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">Simple & rapide</div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-extrabold text-slate-900">
            Votre avis de valeur en 3 étapes
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: Home, t: "Décrivez votre bien", d: "Type, surface, localisation, état... quelques questions guidées suffisent." },
            { icon: Eye, t: "L'IA analyse le marché", d: "Notre moteur croise les prix locaux d'Auvergne-Rhône-Alpes pour vous." },
            { icon: FileText, t: "Recevez votre rapport", d: "Fourchette de prix, analyse détaillée et PDF téléchargeable, gratuitement." },
          ].map((s, i) => (
            <motion.div
              key={i} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`step-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5">
                <s.icon className="w-6 h-6 text-[#2563EB]" strokeWidth={1.5} />
              </div>
              <div className="text-sm font-bold text-slate-300 mb-2">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{s.t}</h3>
              <p className="text-slate-600 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <CustomProject />

      {/* ESTIMATION INTÉGRÉE */}
      <section id="estimation" className="scroll-mt-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-2xl mx-auto text-center mb-4">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">Estimation gratuite</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-extrabold text-slate-900">
              Estimez votre bien maintenant
            </h2>
            <p className="mt-3 text-slate-600">Répondez à quelques questions, recevez votre avis de valeur et son rapport PDF.</p>
          </div>
          <Estimation embedded />
        </div>
      </section>

      {/* TRUST / REGION */}
      <section className="bg-slate-900 grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-5">
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-extrabold text-white leading-tight">
              Une expertise dédiée à votre région
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              De la métropole lyonnaise aux stations des Alpes, du bassin grenoblois aux volcans
              d'Auvergne, nous connaissons les spécificités de chaque marché local.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { icon: Building2, l: "Ville" },
                { icon: Mountain, l: "Montagne" },
                { icon: Home, l: "Périurbain" },
              ].map((x, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <x.icon className="w-6 h-6 text-blue-400 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-xs text-slate-300">{x.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <img src={LYON_IMG} alt="Lyon" className="rounded-2xl h-64 w-full object-cover" />
            <img src={CHALET_IMG} alt="Chalet Alpes" className="rounded-2xl h-64 w-full object-cover mt-8" />
          </div>
        </div>
      </section>

      {/* CARTE DES PRIX AU M² */}
      <section id="carte" className="scroll-mt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl mb-10">
          <div className="flex items-center gap-2 text-[#2563EB] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            <MapPin className="w-4 h-4" strokeWidth={2} /> Carte des prix
          </div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-extrabold text-slate-900">
            Le prix au m² dans votre ville
          </h2>
          <p className="mt-3 text-slate-600">
            Explorez les prix moyens au mètre carré partout en Auvergne-Rhône-Alpes. Cliquez sur un point.
          </p>
        </div>
        <PriceMap height="500px" />
      </section>

      {/* JEU : LE JUSTE PRIX IMMO */}
      <section id="jeu" className="scroll-mt-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C85A40] mb-3">Le jeu</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight font-extrabold text-slate-900">
              Le Juste Prix Immo 🏠
            </h2>
            <p className="mt-3 text-slate-600">
              Saurez-vous deviner la valeur de ce bien en Auvergne-Rhône-Alpes ? Testez votre flair immobilier !
            </p>
          </div>
          <JustePrixGame embedded />
        </div>
      </section>

      <LatestArticles />


      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h2 className="font-display text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900 max-w-2xl mx-auto">
          Prêt à découvrir la valeur de votre bien ?
        </h2>
        <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto">
          Sans engagement. Gratuit. Résultat immédiat avec rapport téléchargeable.
        </p>
        <a
          href="#estimation"
          data-testid="cta-estimate-btn"
          className="inline-flex items-center gap-2 mt-8 bg-[#2563EB] text-white hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition-all shadow-lg shadow-blue-500/20"
        >
          Lancer mon estimation <ArrowRight className="w-5 h-5" strokeWidth={2} />
        </a>
      </section>

      <Footer />
    </div>
  );
}
